import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import React, { useCallback } from 'react';

import useCopyClipboard from '../../../hooks/useCopyClipboard';
import { IReport } from '../../../types/reports';
import { getClaimUrl } from '../../../utils/reports';

interface IProps {
  anchorEl: HTMLElement | null;
  menuPosition?: { top: number; left: number } | null;
  selectedItem: IReport | null;
  isNonCancelingReportStatus: (status: string) => boolean;
  canCancelReport: boolean;
  canDeleteReport: boolean;
  canCopyShippingUrl: () => boolean;
  canCopyTrackingUrl: () => boolean;
  onClose: () => void;
  onCancelReport: () => void;
  onDeleteReport: () => void;
}

export const ReportsContextMenu = ({
  anchorEl,
  menuPosition,
  selectedItem,
  isNonCancelingReportStatus,
  canCancelReport,
  canDeleteReport,
  canCopyShippingUrl,
  canCopyTrackingUrl,
  onClose,
  onCancelReport,
  onDeleteReport,
}: IProps) => {
  const { setCopied } = useCopyClipboard({ successDuration: 1000, toastDuration: 1000 });

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      onClose();
    },
    [onClose],
  );

  const handleCopyShippingUrl = useCallback(() => {
    setCopied(getClaimUrl(selectedItem), 'Claim URL');
    onClose();
  }, [onClose, selectedItem, setCopied]);

  const handleCopyTrackingUrl = useCallback(() => {
    setCopied(selectedItem?.tracking_url || '', 'Tracking URL');
    onClose();
  }, [onClose, selectedItem, setCopied]);

  const handleCancelReport = useCallback(() => {
    onCancelReport();
    onClose();
  }, [onClose, onCancelReport]);

  const handleDeleteReport = useCallback(() => {
    onDeleteReport();
    onClose();
  }, [onClose, onDeleteReport]);

  return (
    <Menu
      anchorEl={anchorEl}
      open={!!(anchorEl || menuPosition)}
      onClose={onClose}
      onContextMenu={handleContextMenu}
      anchorReference={menuPosition ? 'anchorPosition' : 'anchorEl'}
      anchorPosition={menuPosition || undefined}
      autoFocus={false}
      disableAutoFocus={true}
      disableEnforceFocus={true}
      disableRestoreFocus={true}
    >
      {canCopyShippingUrl() && (
        <MenuItem onClick={handleCopyShippingUrl}>
          <ListItemIcon>
            <FileCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy claim link URL</ListItemText>
        </MenuItem>
      )}

      {canCopyTrackingUrl() && (
        <MenuItem onClick={handleCopyTrackingUrl}>
          <ListItemIcon>
            <LocationOnIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy tracking URL</ListItemText>
        </MenuItem>
      )}

      {canCancelReport && (
        <MenuItem disabled={isNonCancelingReportStatus(selectedItem?.status ?? '')} onClick={handleCancelReport}>
          <ListItemIcon>
            <CancelIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Cancel send</ListItemText>
        </MenuItem>
      )}

      {canDeleteReport && (
        <MenuItem onClick={handleDeleteReport}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete send</ListItemText>
        </MenuItem>
      )}
    </Menu>
  );
};
